import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, XCircle, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentStatusModal = ({ status, sessionId, onClose }) => {
  const [paymentData, setPaymentData] = useState(null);
  const [isPolling, setIsPolling] = useState(true);

  useEffect(() => {
    if (status === "success" && sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setIsPolling(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, sessionId]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setIsPolling(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sid}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setPaymentData(data);
        setIsPolling(false);
        return;
      } else if (data.status === "expired") {
        setIsPolling(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch {
      setIsPolling(false);
    }
  };

  const isSuccess = status === "success";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          data-testid="payment-status-modal"
          className="relative glass-card w-full max-w-md p-8 text-center"
        >
          {/* Close button */}
          <button
            data-testid="payment-status-close"
            onClick={onClose}
            className="absolute top-4 right-4 text-[#94a3b8] hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Icon */}
          <div className={`w-20 h-20 mx-auto mb-6 flex items-center justify-center ${
            isSuccess ? "bg-green-500/20" : "bg-red-500/20"
          }`}>
            {isPolling ? (
              <Loader2 className="w-10 h-10 text-[#3b82f6] animate-spin" />
            ) : isSuccess ? (
              <CheckCircle className="w-10 h-10 text-green-500" />
            ) : (
              <XCircle className="w-10 h-10 text-red-500" />
            )}
          </div>

          {/* Content */}
          {isPolling ? (
            <>
              <h3 className="text-2xl font-bold text-white mb-2">
                Procesando Pago
              </h3>
              <p className="text-[#94a3b8] mb-6">
                Por favor espera mientras confirmamos tu pago...
              </p>
            </>
          ) : isSuccess ? (
            <>
              <h3 
                data-testid="payment-success-title"
                className="text-2xl font-bold text-white mb-2"
              >
                ¡Pago Exitoso!
              </h3>
              <p className="text-[#94a3b8] mb-6">
                Gracias por tu compra. Recibirás un correo con los detalles 
                de acceso a tu sistema.
              </p>
              {paymentData && (
                <div className="glass-card p-4 mb-6">
                  <p className="text-[#94a3b8] text-sm">Plan adquirido:</p>
                  <p className="text-white font-semibold">
                    {paymentData.metadata?.package_name || "Sistema Operativo"}
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              <h3 
                data-testid="payment-cancelled-title"
                className="text-2xl font-bold text-white mb-2"
              >
                Pago Cancelado
              </h3>
              <p className="text-[#94a3b8] mb-6">
                El proceso de pago fue cancelado. Puedes intentarlo nuevamente 
                cuando estés listo.
              </p>
            </>
          )}

          {/* CTA */}
          <button
            data-testid="payment-status-cta"
            onClick={onClose}
            className="btn-primary"
          >
            <span>{isSuccess ? "Continuar" : "Volver"}</span>
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PaymentStatusModal;
